/**
 * Felülbírálatok — the overrides report of handover rev7 §11.11.9.
 *
 * "The limits are learned from what staff actually do. The console carries
 * one report, filterable by month, occasion and staff member."
 *
 * Four blocks, in §11.11.9's own order, and a fifth thing it asks for in its
 * last paragraph: the share of staff versions each code is used on, because
 * "if an override code is used on a large share of staff orders, the limit
 * behind it is wrong". The report says so and stops there — the sentence
 * after that one is "Nothing adjusts itself", and nothing here does.
 */

import { useState } from 'react'

import { asDate, fmt, Loading, useConsoleData } from '../common.tsx'

interface Report {
  from: string
  to: string
  occasion: string | null
  staff_filter: string | null
  staff_versions: number
  counts: {
    code: string
    label_hu: string
    rule_hu: string
    touches_kitchen: boolean
    count: number
    orders: number
    share_of_staff_versions_pct: number | null
  }[]
  quantity_deviation: {
    occasion: string
    occasion_label: string | null
    lines: number
    suggested_portions: number
    ordered_portions: number
    deviation_pct: number | null
  }[]
  price_adjustments: {
    count: number
    total_amount: number
    reasons: {
      kind: string
      amount: number | null
      pct: number | null
      reason: string
      by: string | null
      order_id: string
      version: number
    }[]
  }
  after_the_event: {
    order_id: string
    order_ref: string
    event_date: string
    occasion: string | null
    overrides: string[] | null
    quantity_verdict: string | null
    guests_came: number | null
    leftover_portions: number
    leftovers_observed: boolean
  }[]
}

/** §11.10's five answers on quantity, in its own words. */
const VERDICT_HU: Record<string, string> = {
  sok_maradt: 'sok maradt',
  kicsit_maradt: 'kicsit maradt',
  epp_eleg: 'épp elég volt',
  hamar_elfogyott: 'hamar elfogyott',
  keves_volt: 'kevés volt',
}

/** The first day of the month `offset` months before this one. */
function monthStart(offset: number): string {
  const now = new Date()
  const date = new Date(now.getFullYear(), now.getMonth() - offset, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`
}

function monthEnd(start: string): string {
  const [year, month] = start.split('-').map(Number)
  const date = new Date(year!, month!, 0)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${
    String(date.getDate()).padStart(2, '0')
  }`
}

export function Overrides(): JSX.Element {
  const [month, setMonth] = useState(() => monthStart(0))
  const [occasion, setOccasion] = useState('')
  const [staff, setStaff] = useState('')

  const { data, error, loading } = useConsoleData<Report>('overrides_report', {
    from: month,
    to: monthEnd(month),
    ...(occasion === '' ? {} : { occasion }),
    ...(staff === '' ? {} : { staff }),
  })

  return (
    <section data-testid="view-overrides">
      <h2>Felülbírálatok</h2>
      <p className="lead">
        §11.11.9: a korlátokat abból tanuljuk, amit a munkatársak ténylegesen csinálnak. Ez a
        jelentés számol és beszámol — semmi nem állítja át magát. Ha egy kód a munkatársi
        verziók nagy részén szerepel, a mögötte álló korlát a rossz, és az a paraméter
        gazdájához tartozik a kalibrációs áttekintésen.
      </p>

      <div className="card" data-testid="overrides-filters">
        <div className="grid">
          <div>
            <label htmlFor="ov-month">Hónap</label>
            <select
              id="ov-month" value={month} onChange={(e) => setMonth(e.target.value)}
              data-testid="ov-month"
            >
              {[0, 1, 2, 3, 4, 5].map((offset) => (
                <option key={offset} value={monthStart(offset)}>{monthStart(offset).slice(0, 7)}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ov-occasion">Alkalom</label>
            <input
              id="ov-occasion" type="text" placeholder="mind"
              value={occasion} onChange={(e) => setOccasion(e.target.value)}
              data-testid="ov-occasion"
            />
          </div>
          <div>
            <label htmlFor="ov-staff">Munkatárs (e-mail)</label>
            <input
              id="ov-staff" type="text" placeholder="mind"
              value={staff} onChange={(e) => setStaff(e.target.value)}
              data-testid="ov-staff"
            />
          </div>
        </div>
      </div>

      <Loading loading={loading} error={error} />

      {data !== null && (
        <>
          <p className="note" data-testid="overrides-scope">
            {asDate(data.from)} – {asDate(data.to)}: {fmt.n(data.staff_versions)} munkatársi verzió.
          </p>

          <div className="card">
            <h3>Felülbírálatok kódonként</h3>
            <table>
              <thead>
                <tr>
                  <th>Kód</th><th>Szabály</th>
                  <th className="num">Esetek</th><th className="num">Rendelések</th>
                  <th className="num">A verziók hány %-án</th>
                </tr>
              </thead>
              <tbody>
                {data.counts.map((row) => (
                  <tr key={row.code} data-testid={`count-${row.code}`}>
                    <td>
                      {row.label_hu}
                      {row.touches_kitchen && <div className="tag warn">a konyha is látja</div>}
                    </td>
                    <td>{row.rule_hu}</td>
                    <td className="num" data-testid={`count-n-${row.code}`}>{fmt.n(row.count)}</td>
                    <td className="num">{fmt.n(row.orders)}</td>
                    <td className="num">
                      {row.share_of_staff_versions_pct === null
                        ? '—'
                        : `${fmt.n(row.share_of_staff_versions_pct, 1)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card">
            <h3>Mennyiségi eltérés alkalmanként</h3>
            <p className="note">
              Csak a kézzel beállított tételek (§11.11.9): a rendelt adagszám a javasolthoz képest.
            </p>
            {data.quantity_deviation.length === 0
              ? <p className="note" data-testid="deviation-empty">Ebben az időszakban nincs kézzel beállított tétel.</p>
              : (
                <table>
                  <thead>
                    <tr>
                      <th>Alkalom</th><th className="num">Tételek</th>
                      <th className="num">Javasolt</th><th className="num">Rendelt</th>
                      <th className="num">Eltérés</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.quantity_deviation.map((row) => (
                      <tr key={row.occasion} data-testid={`deviation-${row.occasion}`}>
                        <td>{row.occasion_label ?? row.occasion}</td>
                        <td className="num">{fmt.n(row.lines)}</td>
                        <td className="num">{fmt.n(row.suggested_portions)}</td>
                        <td className="num">{fmt.n(row.ordered_portions)}</td>
                        <td className="num">
                          {row.deviation_pct === null
                            ? '—'
                            : `${row.deviation_pct > 0 ? '+' : ''}${fmt.n(row.deviation_pct, 1)}%`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>

          <div className="card">
            <h3>Ár-korrekciók</h3>
            <p className="note" data-testid="adjustments-summary">
              {fmt.n(data.price_adjustments.count)} tétel, összesen{' '}
              {fmt.money(data.price_adjustments.total_amount)}.
            </p>
            {data.price_adjustments.reasons.length > 0 && (
              <table>
                <thead>
                  <tr><th>Típus</th><th className="num">Összeg</th><th className="num">%</th><th>Indoklás</th><th>Ki</th></tr>
                </thead>
                <tbody>
                  {data.price_adjustments.reasons.map((row, index) => (
                    <tr key={`${row.order_id}-${row.version}-${index}`}>
                      <td>{row.kind === 'discount' ? 'kedvezmény' : 'felár'}</td>
                      <td className="num">{row.amount === null ? '—' : fmt.money(row.amount)}</td>
                      <td className="num">{row.pct === null ? '—' : `${fmt.n(row.pct, 1)}%`}</td>
                      <td>{row.reason}</td>
                      <td>{row.by ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="card">
            <h3>A rendezvény után</h3>
            <p className="note">
              §11.11.9: a felülbírálatot kapott rendelések kérdőíves válasza a mennyiségről, és a
              feljegyzett maradék. §11.10: ahol nincs megfigyelés, ott nem nulla van, hanem nincs
              adat — drop-off esetén ez a szokásos.
            </p>
            {data.after_the_event.length === 0
              ? <p className="note" data-testid="after-empty">Ebben az időszakban még nincs lezárt, felülbírálatot kapott rendelés.</p>
              : (
                <table>
                  <thead>
                    <tr>
                      <th>Rendelés</th><th>Dátum</th><th>Felülbírálatok</th>
                      <th>Mennyiség</th><th className="num">Hányan jöttek</th><th className="num">Maradék</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.after_the_event.map((row) => (
                      <tr key={row.order_id} data-testid={`after-${row.order_ref}`}>
                        <td>{row.order_ref}</td>
                        <td>{asDate(row.event_date)}</td>
                        <td>{(row.overrides ?? []).join(' · ')}</td>
                        <td>
                          {row.quantity_verdict === null
                            ? '—'
                            : VERDICT_HU[row.quantity_verdict] ?? row.quantity_verdict}
                        </td>
                        <td className="num">{row.guests_came === null ? '—' : fmt.n(row.guests_came)}</td>
                        <td className="num">
                          {row.leftovers_observed ? fmt.n(row.leftover_portions) : 'nincs adat'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </>
      )}
    </section>
  )
}
